<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import ButtonModeOptions from './ButtonModeOptions.svelte';
	import InitialStateOptions from './InitialStateOptions.svelte';
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';
	import { Button, Container, toast } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';

	export let device: Device;

	const id = real_id(device.id);
	const componentPath = get_gen_2_component_channel_structure(device);
	// Input type is fixed to Button on these models (the local web locks it), so
	// there is no type selector here. Modes are limited to the two the firmware
	// supports: Attached (momentary) and Detached.
	const typeValue = 'button';

	const buttonModeOptions = {
		momentary: {
			type: 'button',
			text: $format('btn_mode_attached_desc', {
				default: 'One button turns the output on, the other turns it off.',
			}),
			title: $format('btn_mode_attached_title', { default: 'Attached' }),
			description: $format('btn_mode_attached_desc', {
				default: 'One button turns the output on, the other turns it off.',
			}),
		},
		detached: {
			type: 'any',
			text: $format('btn_mode_detached_description', {
				default:
					'Set Shelly device to be in "Detached" switch mode - switch is separated from the relays.',
			}),
			title: $format('btn_mode_detached_title', { default: 'Detached switch' }),
			description: $format('btn_mode_detached_desc', {
				default: 'Switch is separated from the relays.',
			}),
		},
	};

	const initialStateOptions = {
		on: {
			type: 'any',
			text: $format('init_state_on_description', {
				default: 'Configure Shelly device to Turn ON, when it has power.',
			}),
			title: $format('init_state_on_title', { default: 'Turn on' }),
			description: $format('init_state_on_desc', {
				default: 'Automatically turns the device ON when power is restored.',
			}),
		},
		off: {
			type: 'any',
			text: $format('init_state_off_description', {
				default: 'Configure Shelly device to Turn OFF, when it has power.',
			}),
			title: $format('init_state_off_title', { default: 'Turn off' }),
			description: $format('init_state_off_desc', {
				default: 'Keeps the device OFF when power is restored.',
			}),
		},
		restore_last: {
			type: 'any',
			text: $format('init_state_report_last_description', {
				default:
					'Configure Shelly device to Restore the last mode it was in, when it has power.',
			}),
			title: $format('init_state_restore_last_title', { default: 'Restore last state' }),
			description: $format('init_state_restore_last_desc', {
				default: 'Returns the device to its previous state when power is restored.',
			}),
		},
	};

	let settings = readDeviceSettings(id);
	let modeValue = settings[componentPath]?.in_mode;
	let cacheModeValue = modeValue;
	let initialStateValue = settings[componentPath]?.initial_state;
	let cacheInitialStateValue = initialStateValue;
	let loading = false;

	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore ?? settings;
	$: reactiveStateValue = initialStateValue;

	const sentRequests = async () => {
		if (cacheInitialStateValue === initialStateValue && cacheModeValue === modeValue) {
			return;
		}
		loading = true;
		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			{
				id: device.channel,
				config: {
					in_mode: modeValue,
					initial_state: initialStateValue,
				},
			},
		);

		if (res.isok) {
			if (cacheModeValue !== modeValue) {
				toast.open({
					type: 'success',
					title: $format('btn_mode_scc_toast', {
						values: { mode_value: modeValue },
						default: 'Button mode set to {mode_value}.',
					}),
				});
				cacheModeValue = modeValue;
				if (settings[componentPath]) {
					settings[componentPath].in_mode = modeValue;
					devices_settings.update_devices_settings(id, settings);
				}
			}
			if (cacheInitialStateValue !== initialStateValue) {
				toast.open({
					type: 'success',
					title: $format('init_state_scc_toast', {
						values: { initial_state_value: $format(initialStateValue) },
						default: 'Initial state set to {initial_state_value}.',
					}),
				});
				cacheInitialStateValue = initialStateValue;
				if (settings[componentPath]) {
					settings[componentPath].initial_state = initialStateValue;
					devices_settings.update_devices_settings(id, settings);
				}
			}
		} else {
			if (cacheModeValue !== modeValue) {
				modeValue = cacheModeValue;
				toast.open({
					type: 'danger',
					title: $format('btn_mode_err_toast', {
						default: 'Error! Could not change button mode.',
					}),
				});
			}
			if (cacheInitialStateValue !== initialStateValue) {
				initialStateValue = cacheInitialStateValue;
				toast.open({
					type: 'danger',
					title: $format('init_state_err_toast', {
						default: 'Could not change input initial state.',
					}),
				});
			}
		}
		loading = false;
	};
</script>

{#if $showNewLayout}
	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<ButtonModeOptions
			{typeValue}
			bind:modeValue
			{buttonModeOptions}
			_modeValue={modeValue}
			bind:reactiveStateValue
		/>
		<InitialStateOptions
			{typeValue}
			bind:initialStateValue
			mandatoryUpdate={false}
			{initialStateOptions}
			{modeValue}
		/>
	</Container>
	<div class="mb-s">
		<Button
			class="mt-l"
			{loading}
			size="m"
			width="full"
			on:click={sentRequests}
			aria-label={$format('button_save', { default: 'Save' })}
		>
			{$format('button_save', { default: 'Save' })}
		</Button>
	</div>
{:else}
	<ButtonModeOptions
		{typeValue}
		bind:modeValue
		{buttonModeOptions}
		_modeValue={modeValue}
		bind:reactiveStateValue
	/>
	<InitialStateOptions
		{typeValue}
		bind:initialStateValue
		mandatoryUpdate={false}
		{initialStateOptions}
		{modeValue}
	/>
	<Button
		class="mt-s"
		{loading}
		size="m"
		width="full"
		on:click={sentRequests}
		aria-label={$format('button_save', { default: 'Save' })}
	>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
